import React from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
  Box,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Grid,
  FormControlLabel,
  Switch,
  Typography,
  Paper,
  Autocomplete,
} from '@mui/material';
import { Location, LocationFormData } from '../../types/location.types';

import { Facility } from '@/features/facilities/types/facility.types';

interface LocationFormProps {
  initialValues?: Partial<Location>;
  onSubmit: (values: LocationFormData) => Promise<void>;
  onCancel: () => void;
  isSubmitting?: boolean;
  title?: string;
  facilities: Facility[];
  parentLocations: Location[];
  dropdownsLoading?: boolean;
}

const locationTypes = [
  { value: 'BUILDING', label: 'Building' },
  { value: 'FLOOR', label: 'Floor' },
  { value: 'ROOM', label: 'Room' },
  { value: 'WARD', label: 'Ward' },
  { value: 'DEPARTMENT', label: 'Department' },
  { value: 'OTHER', label: 'Other' },
];

const locationSubTypes = [
  { value: 'GENERAL', label: 'General' },
  { value: 'ICU', label: 'ICU' },
  { value: 'EMERGENCY', label: 'Emergency' },
  { value: 'OPERATION', label: 'Operation' },
  { value: 'CONSULTATION', label: 'Consultation' },
  { value: 'ADMIN', label: 'Admin' },
  { value: 'LAB', label: 'Lab' },
  { value: 'PHARMACY', label: 'Pharmacy' },
  { value: 'STORAGE', label: 'Storage' },
  { value: 'OTHER', label: 'Other' },
];

const validationSchema = Yup.object({
  code: Yup.string().required('Code is required'),
  name: Yup.string().required('Name is required'),
  locationType: Yup.string().required('Location type is required'),
  locationSubType: Yup.string(),
  description: Yup.string(),
  parentId: Yup.string(),
  facilityId: Yup.string().required('Facility is required'),
  capacity: Yup.number().min(0, 'Capacity must be a positive number'),
  floorPlanUrl: Yup.string().url('Must be a valid URL'),
  barcode: Yup.string(),
  qrCode: Yup.string(),
  isActive: Yup.boolean().default(true),
  status: Yup.boolean().default(true),
});

export const LocationForm: React.FC<LocationFormProps> = ({
  initialValues = {
    code: '',
    name: '',
    locationType: '',
    locationSubType: '',
    description: '',
    facilityId: '',
    isActive: true,
    status: true,
  },
  onSubmit,
  onCancel,
  isSubmitting = false,
  title = 'Location Details',
  facilities = [],
  parentLocations = [],
  dropdownsLoading = false,
}) => {
  const formik = useFormik({
    initialValues: {
      ...initialValues,
      capacity: initialValues.capacity || undefined,
    } as LocationFormData,
    validationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        await onSubmit(values);
      } finally {
        setSubmitting(false);
      }
    },
  });

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        {title}
      </Typography>
      <form onSubmit={formik.handleSubmit}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              id="code"
              name="code"
              label="Code"
              value={formik.values.code}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.code && Boolean(formik.errors.code)}
              helperText={formik.touched.code && formik.errors.code}
              margin="normal"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              id="name"
              name="name"
              label="Name"
              value={formik.values.name}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.name && Boolean(formik.errors.name)}
              helperText={formik.touched.name && formik.errors.name}
              margin="normal"
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <FormControl fullWidth margin="normal" error={formik.touched.locationType && Boolean(formik.errors.locationType)}>
              <InputLabel id="location-type-label">Location Type</InputLabel>
              <Select
                labelId="location-type-label"
                id="locationType"
                name="locationType"
                value={formik.values.locationType}
                label="Location Type"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              >
                {locationTypes.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </Select>
              {formik.touched.locationType && formik.errors.locationType && (
                <FormHelperText>{formik.errors.locationType}</FormHelperText>
              )}
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth margin="normal">
              <InputLabel id="location-subtype-label">Location Subtype</InputLabel>
              <Select
                labelId="location-subtype-label"
                id="locationSubType"
                name="locationSubType"
                value={formik.values.locationSubType || ''}
                label="Location Subtype"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              >
                {locationSubTypes.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              id="description"
              name="description"
              label="Description"
              multiline
              rows={3}
              value={formik.values.description || ''}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              margin="normal"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <Autocomplete
  fullWidth
  id="facilityId"
  options={facilities}
  getOptionLabel={(option) => option.name}
  value={facilities.find(f => f.id === formik.values.facilityId) || null}
  onChange={(_, value) => formik.setFieldValue('facilityId', value ? value.id : '')}
  onBlur={formik.handleBlur}
  loading={dropdownsLoading}
  disabled={dropdownsLoading}
  renderInput={(params) => (
    <TextField
      {...params}
      label="Facility"
      margin="normal"
      required
      error={formik.touched.facilityId && Boolean(formik.errors.facilityId)}
      helperText={formik.touched.facilityId && formik.errors.facilityId}
      InputProps={{
        ...params.InputProps,
        endAdornment: (
          <>
            {dropdownsLoading ? <span className="MuiCircularProgress-root MuiCircularProgress-colorPrimary MuiCircularProgress-indeterminate" style={{ width: 20, height: 20, marginRight: 8 }} /> : null}
            {params.InputProps.endAdornment}
          </>
        ),
      }}
    />
  )}
/>
          </Grid>

          <Grid item xs={12} md={6}>
            <Autocomplete
  fullWidth
  id="parentId"
  options={parentLocations}
  getOptionLabel={(option) => option.name}
  value={parentLocations.find(l => l.id === formik.values.parentId) || null}
  onChange={(_, value) => formik.setFieldValue('parentId', value ? value.id : '')}
  onBlur={formik.handleBlur}
  loading={dropdownsLoading}
  disabled={dropdownsLoading}
  renderInput={(params) => (
    <TextField
      {...params}
      label="Parent Location (Optional)"
      margin="normal"
      InputProps={{
        ...params.InputProps,
        endAdornment: (
          <>
            {dropdownsLoading ? <span className="MuiCircularProgress-root MuiCircularProgress-colorPrimary MuiCircularProgress-indeterminate" style={{ width: 20, height: 20, marginRight: 8 }} /> : null}
            {params.InputProps.endAdornment}
          </>
        ),
      }}
    />
  )}
/>
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              id="capacity"
              name="capacity"
              label="Capacity"
              type="number"
              value={formik.values.capacity || ''}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.capacity && Boolean(formik.errors.capacity)}
              helperText={formik.touched.capacity && formik.errors.capacity}
              margin="normal"
              InputProps={{ inputProps: { min: 0 } }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              id="floorPlanUrl"
              name="floorPlanUrl"
              label="Floor Plan URL"
              value={formik.values.floorPlanUrl || ''}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.floorPlanUrl && Boolean(formik.errors.floorPlanUrl)}
              helperText={formik.touched.floorPlanUrl && formik.errors.floorPlanUrl}
              margin="normal"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={formik.values.isActive}
                  onChange={(e) => formik.setFieldValue('isActive', e.target.checked)}
                  name="isActive"
                  color="primary"
                />
              }
              label="Active"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={formik.values.status}
                  onChange={(e) => formik.setFieldValue('status', e.target.checked)}
                  name="status"
                  color="primary"
                />
              }
              label="Status"
            />
          </Grid>

          <Grid item xs={12}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
              <Button onClick={onCancel} variant="outlined" disabled={isSubmitting}>
                Cancel
              </Button>
              <Button type="submit" variant="contained" color="primary" disabled={isSubmitting}>
                {isSubmitting ? 'Saving...' : 'Save'}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </form>
    </Paper>
  );
};

export default LocationForm;
